"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.BasisTheory = void 0;

var _applicationTemplates = require("./application-templates");

var _applications = require("./applications");

var _common = require("./common");

var _elements = require("./elements");

var _constants = require("./elements/constants");

var _logs = require("./logs");

var _permissions = require("./permissions");

var _proxies = require("./proxies");

var _reactorFormulas = require("./reactor-formulas");

var _reactors = require("./reactors");

var _sessions = require("./sessions");

var _tenants = require("./tenants");

var _threeds = require("./threeds");

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const defaultInitOptions = {
  apiBaseUrl: _common.DEFAULT_BASE_URL,
  elements: false,
  appInfo: {}
};

class BasisTheory {
  constructor() {
    _defineProperty(this, "_initStatus", 'not-started');
  }

  async init(apiKey, options = {}) {
    if (this._initStatus !== 'not-started' && this._initStatus !== 'error') {
      throw new Error('This BasisTheory instance has been already initialized.');
    }

    this._initStatus = 'in-progress';

    try {
      this._initOptions = Object.freeze({ ...defaultInitOptions,
        ...options
      });
      let baseUrl = this._initOptions.apiBaseUrl;

      try {
        const baseUrlObject = new URL(this.initOptions.apiBaseUrl);

        if (baseUrlObject.hostname === 'localhost') {
          baseUrlObject.protocol = 'http';
        } else {
          baseUrlObject.protocol = 'https';
        }

        baseUrl = baseUrlObject.toString().replace(/\/$/u, '');
      } catch {
        throw new Error('Invalid format for the given API base url.');
      }

      const appInfo = this._initOptions.appInfo;

      if (this._initOptions.elements) {
        await this.loadElements(apiKey);
      }

      this._tokens = new ((0, _elements.delegateTokens)(this._elements))({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.tokens, baseUrl).toString(),
        appInfo
      });
      this._tokenize = new ((0, _elements.delegateTokenize)(this._elements))({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.tokenize, baseUrl).toString(),
        appInfo
      });
      this._applications = new _applications.BasisTheoryApplications({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.applications, baseUrl).toString(),
        appInfo
      });
      this._applicationTemplates = new _applicationTemplates.BasisTheoryApplicationTemplates({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.applicationTemplates, baseUrl).toString(),
        appInfo
      });
      this._tenants = new _tenants.BasisTheoryTenants({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.tenants, baseUrl).toString(),
        appInfo
      });
      this._logs = new _logs.BasisTheoryLogs({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.logs, baseUrl).toString(),
        appInfo
      });
      this._reactorFormulas = new _reactorFormulas.BasisTheoryReactorFormulas({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.reactorFormulas, baseUrl).toString(),
        appInfo
      });
      this._reactors = new _reactors.BasisTheoryReactors({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.reactors, baseUrl).toString(),
        appInfo
      });
      this._permissions = new _permissions.BasisTheoryPermissions({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.permissions, baseUrl).toString(),
        appInfo
      });
      this._proxies = new _proxies.BasisTheoryProxies({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.proxies, baseUrl).toString(),
        appInfo
      });
      this._proxy = new ((0, _elements.delegateProxy)(this._elements))({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.proxy, baseUrl).toString(),
        appInfo
      });
      this._sessions = new _sessions.BasisTheorySessions({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.sessions, baseUrl).toString(),
        appInfo
      });
      this._threeds = new _threeds.BasisTheoryThreeDS({
        apiKey,
        baseURL: new URL(_common.CLIENT_BASE_PATHS.threeds, baseUrl).toString(),
        appInfo
      });
      this._initStatus = 'done';
    } catch (error) {
      this._initStatus = 'error';
      throw error;
    }

    return this;
  }

  createElement(type, options) {
    if (!this._elements) {
      throw new Error(_constants.ELEMENTS_INIT_ERROR_MESSAGE);
    }

    return this._elements.createElement(type, options);
  }

  tokenize(tokens, options) {
    return (0, _common.assertInit)(this._tokenize).tokenize(tokens, options);
  }

  async loadElements(apiKey) {
    let elementsBaseUrl;

    try {
      elementsBaseUrl = new URL(this.initOptions.elementsBaseUrl || _common.DEFAULT_ELEMENTS_BASE_URL);
    } catch {
      throw new Error('Invalid format for the given Elements base url.');
    }

    const elements = await (0, _elements.loadElements)(this.initOptions.elementsClientUrl);
    const elementsUseNgApi = this.initOptions.elementsUseNgApi || false;
    await elements.init(apiKey, elementsBaseUrl.toString().replace(/\/$/u, ''), elementsUseNgApi);
    this.elements = elements;
  } // these should be set by the init call only.

  /* eslint-disable accessor-pairs */


  get initOptions() {
    return (0, _common.assertInit)(this._initOptions);
  }

  get tokens() {
    return (0, _common.assertInit)(this._tokens);
  }

  get applications() {
    return (0, _common.assertInit)(this._applications);
  }

  get applicationTemplates() {
    return (0, _common.assertInit)(this._applicationTemplates);
  }

  get client() {
    if (this._elements) {
      var _this$_elements;

      return (_this$_elements = this._elements) === null || _this$_elements === void 0 ? void 0 : _this$_elements.client;
    } // eslint-disable-next-line no-console


    console.error('Elements are not initialized. Either initialize elements or use a regular HTTP client if no elements are needed.');
    return undefined;
  }

  get tenants() {
    return (0, _common.assertInit)(this._tenants);
  }

  get logs() {
    return (0, _common.assertInit)(this._logs);
  }
  /**
   * @deprecated Reactor Formulas are now deprecated and will be removed in a future release.
   * @description We have introduced a `code` property for Reactors to replace Formula's code. For more details visit [our API reference](https://developers.basistheory.com/docs/api/reactors#create-reactor).
   */


  get reactorFormulas() {
    return (0, _common.assertInit)(this._reactorFormulas);
  }

  get reactors() {
    return (0, _common.assertInit)(this._reactors);
  }

  get permissions() {
    return (0, _common.assertInit)(this._permissions);
  }

  get proxies() {
    return (0, _common.assertInit)(this._proxies);
  }

  get proxy() {
    return (0, _common.assertInit)(this._proxy);
  }

  get sessions() {
    return (0, _common.assertInit)(this._sessions);
  }

  get threeds() {
    return (0, _common.assertInit)(this._threeds);
  }
  /* eslint-enable accessor-pairs */

  /**
   * @deprecated
   */


  get elements() {
    return (0, _common.assertInit)(this._elements);
  }

  set elements(elements) {
    this._elements = elements;
  }

}

exports.BasisTheory = BasisTheory;